/**
 * Fornecedores (17 §3.1) e grupos de produto.
 * CNPJ opcional (a BASE DE DADOS não tem), validado quando informado; UF obrigatória (origem do ICMS).
 */
import { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  EmailField,
  FunctionField,
  List,
  ReferenceField,
  ReferenceManyField,
  SelectInput,
  SimpleForm,
  SimpleList,
  TextField,
  TextInput,
  useNotify,
  email,
  regex,
  required,
  useRecordContext,
} from "react-admin";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/AddOutlined";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/ManageSearchOutlined";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { consultarCnpj, type RespostaCnpj } from "../lib/cnpj";
import { cnpjValido, cpfValido, formatarCnpj, formatarCpf, soDigitos } from "@sobral/dominio";
import { AcoesLista, BarraFormulario, EntradaListaCrescente, EntradaTelefone, LinkWhatsapp, useCelular, vazioParaNulo } from "../componentes";
import { formatarDataHora, formatarMoeda, formatarNumero, linkWhatsapp, normalizarBusca } from "../lib/formato";
import { ChipSituacaoCotacao } from "./cotacoes";

export const UFS = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RN", "RS", "RJ", "RO", "RR", "SC", "SP", "SE", "TO",
].map((uf) => ({ id: uf, name: uf }));

const validarCnpj = (valor: unknown) =>
  valor === null || valor === undefined || valor === "" || cnpjValido(String(valor)) ? undefined : "CNPJ inválido.";
const digitosOuNulo = (valor: unknown) => {
  const d = soDigitos(String(valor ?? ""));
  return d === "" ? null : d;
};
const validarUrl = regex(/^https?:\/\/\S+$/, "Informe o endereço completo, começando com https://");

/* -------------------------------- Fornecedores -------------------------------- */

const filtrosFornecedor = [
  <TextInput key="busca" source="busca@ilike" label="Pesquisar" parse={normalizarBusca} alwaysOn resettable />,
  <SelectInput key="uf" source="uf" label="UF" choices={UFS} />,
  <SelectInput
    key="ativo"
    source="ativo"
    label="Situação"
    choices={[
      { id: true, name: "Ativos" },
      { id: false, name: "Inativos" },
    ]}
  />,
];

export function FornecedorLista() {
  const celular = useCelular();
  return (
    <List
      title="Fornecedores"
      sort={{ field: "nome", order: "ASC" }}
      filters={filtrosFornecedor}
      filterDefaultValues={{ ativo: true }}
      actions={<AcoesLista />}
      exporter={false}
    >
      {celular ? (
        <SimpleList
          primaryText={(r) => r.nome}
          secondaryText={(r) => [r.uf, r.vendedor].filter(Boolean).join(" · ")}
          tertiaryText={(r) => <LinkWhatsapp numero={r.whatsapp} />}
          linkType="edit"
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick="edit">
          <TextField source="nome" />
          <TextField source="uf" />
          <FunctionField source="cnpj" render={(r) => (r.cnpj ? formatarCnpj(r.cnpj) : "—")} />
          <ReferenceField source="setor_id" reference="setor_fornecedor" link={false} emptyText="—">
            <TextField source="nome" />
          </ReferenceField>
          <TextField source="vendedor" emptyText="—" />
          <FunctionField source="whatsapp" label="WhatsApp" render={(r) => <LinkWhatsapp numero={r.whatsapp} />} />
          <EmailField source="email" emptyText="—" />
          <BooleanField source="ativo" />
        </Datagrid>
      )}
    </List>
  );
}

const meia = { xs: 12, sm: 6 } as const;
const terco = { xs: 12, sm: 4 } as const;

export type { RespostaCnpj };

export type Preencher = (campo: string, valor: string | null | undefined, rotulo: string) => void;

const formatarDocumento = (v: string | null | undefined) => {
  const d = soDigitos(v);
  return d.length === 11 ? formatarCpf(d) : formatarCnpj(v);
};
const validarDocumento = (valor: unknown) => {
  const d = soDigitos(String(valor ?? ""));
  if (d === "") return undefined;
  if (d.length === 11) return cpfValido(d) ? undefined : "CPF inválido.";
  return cnpjValido(d) ? undefined : "CNPJ inválido.";
};

/**
 * Consulta pública do CNPJ (BrasilAPI, gratuita, sem chave) e preenchimento dos campos ainda vazios.
 * Nada que já foi digitado é sobrescrito, exceto os campos listados em `sobrescrever` (cadastro rápido de registro novo,
 * em que o nome veio do texto da busca e a Receita é a fonte).
 */
export function ConsultaCnpj({
  source = "cnpj",
  aceitaCpf = false,
  preencherExtra,
  avisoSituacao = "Verifique antes de comprar deste fornecedor.",
  consultarAoAbrir = false,
  sobrescrever = [],
}: {
  source?: string;
  aceitaCpf?: boolean;
  preencherExtra?: (dados: RespostaCnpj, preencher: Preencher) => void;
  avisoSituacao?: string;
  /** Consulta o CNPJ já preenchido ao abrir (cadastro rápido que chega com o CNPJ lido de um PDF). */
  consultarAoAbrir?: boolean;
  /** Campos que a consulta pode trocar mesmo já preenchidos. */
  sobrescrever?: string[];
} = {}) {
  const { getValues, setValue } = useFormContext();
  const notify = useNotify();
  const [consultando, setConsultando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // CNPJ do registro aberto: não consulta de novo só por abrir a ficha.
  const cnpjAtual = soDigitos(String(useWatch({ name: source }) ?? ""));
  const ultimoConsultado = useRef<string>(consultarAoAbrir ? "" : cnpjAtual);

  // Consulta automática assim que o CNPJ digitado fica completo e válido (D46).
  useEffect(() => {
    if (cnpjAtual.length === 14 && cnpjAtual !== ultimoConsultado.current && cnpjValido(cnpjAtual)) {
      ultimoConsultado.current = cnpjAtual;
      void consultar();
    }
  }, [cnpjAtual]);

  async function consultar() {
    setAviso(null);
    const cnpj = soDigitos(String(getValues(source) ?? ""));
    if (!cnpjValido(cnpj)) {
      notify("Informe um CNPJ válido para consultar.", { type: "warning" });
      return;
    }
    ultimoConsultado.current = cnpj;
    setConsultando(true);
    try {
      const resultado = await consultarCnpj(cnpj);
      if (resultado.situacao === "nao_encontrado") {
        notify("CNPJ não encontrado na base da Receita Federal.", { type: "warning" });
        return;
      }
      if (resultado.situacao === "indisponivel") {
        notify(`Consulta à Receita indisponível no momento (${resultado.detalhe}). Preencha manualmente.`, { type: "warning" });
        return;
      }
      const dados = resultado.dados;
      const preenchidos: string[] = [];
      const preencher = (campo: string, valor: string | null | undefined, rotulo: string) => {
        const atual = getValues(campo);
        const vazio = atual === null || atual === undefined || String(atual).trim() === "";
        if (valor && (vazio || (sobrescrever.includes(campo) && String(atual) !== valor))) {
          setValue(campo, valor, { shouldDirty: true, shouldValidate: true });
          preenchidos.push(rotulo);
        }
      };
      preencher("razao_social", dados.razao_social, "razão social");
      preencher("nome", dados.nome_fantasia || dados.razao_social, "nome");
      preencher("uf", dados.uf, "UF");
      preencher("telefone", soDigitos(dados.ddd_telefone_1) || null, "telefone");
      preencher("email", dados.email ? dados.email.toLowerCase() : null, "e-mail");
      preencherExtra?.(dados, preencher);
      if (dados.descricao_situacao_cadastral && dados.descricao_situacao_cadastral.toUpperCase() !== "ATIVA") {
        setAviso(`Situação cadastral na Receita Federal: ${dados.descricao_situacao_cadastral}. ${avisoSituacao}`);
      }
      notify(
        preenchidos.length > 0
          ? `Dados preenchidos: ${preenchidos.join(", ")}. Confira antes de salvar.`
          : "Consulta concluída. Os campos já estavam preenchidos e não foram alterados.",
        { type: "info" },
      );
    } catch {
      notify("Não foi possível consultar o CNPJ agora. Preencha manualmente.", { type: "warning" });
    } finally {
      setConsultando(false);
    }
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextInput
          source={source}
          label={aceitaCpf ? "CNPJ ou CPF" : "CNPJ"}
          format={aceitaCpf ? formatarDocumento : formatarCnpj}
          parse={digitosOuNulo}
          validate={aceitaCpf ? validarDocumento : validarCnpj}
          sx={{ flex: 1 }}
          helperText="Ao completar o CNPJ, os dados públicos da Receita Federal são preenchidos automaticamente."
        />
        <Button variant="outlined" onClick={consultar} disabled={consultando} startIcon={<SearchIcon />} sx={{ mt: 1, flexShrink: 0 }}>
          {consultando ? "Consultando…" : "Consultar"}
        </Button>
      </Stack>
      {aviso && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {aviso}
        </Alert>
      )}
    </>
  );
}

/** Atalho para abrir a conversa com o número digitado no campo WhatsApp. */
function AbrirConversa() {
  const url = linkWhatsapp(useWatch({ name: "whatsapp" }) as string | null);
  if (!url) return null;
  return (
    <Button size="small" href={url} target="_blank" rel="noopener noreferrer" sx={{ mt: -1, mb: 1 }}>
      Abrir conversa no WhatsApp
    </Button>
  );
}

/**
 * Campos do fornecedor: os mesmos na ficha e no cadastro rápido da cotação (D112, paridade).
 * No rápido, a consulta do CNPJ pode corrigir o que veio da busca e o campo "ativo" não aparece.
 */
export function CamposFornecedor({ rapido = false, consultarAoAbrir = false }: { rapido?: boolean; consultarAoAbrir?: boolean } = {}) {
  return (
    <Grid container columnSpacing={2} sx={{ width: "100%" }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <ConsultaCnpj consultarAoAbrir={consultarAoAbrir} {...(rapido ? { sobrescrever: ["nome", "razao_social", "uf"] } : {})} />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <SelectInput source="uf" choices={UFS} validate={required()} fullWidth helperText="Origem da mercadoria (ICMS)." />
      </Grid>
      <Grid size={meia}>
        <TextInput source="nome" validate={required()} fullWidth helperText="Nome pelo qual o fornecedor é conhecido." />
      </Grid>
      <Grid size={meia}>
        <TextInput source="razao_social" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={terco}>
        <EntradaListaCrescente source="setor_id" reference="setor_fornecedor" rotuloNovo="Setor" />
      </Grid>
      <Grid size={terco}>
        <TextInput source="vendedor" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={terco}>
        <EntradaTelefone source="telefone" fullWidth />
      </Grid>
      <Grid size={terco}>
        <EntradaTelefone source="whatsapp" label="WhatsApp" fullWidth />
        <AbrirConversa />
      </Grid>
      <Grid size={terco}>
        <TextInput source="email" parse={vazioParaNulo} validate={email("E-mail inválido.")} fullWidth />
      </Grid>
      <Grid size={terco}>
        <TextInput source="url" parse={vazioParaNulo} validate={validarUrl} fullWidth />
      </Grid>
      <Grid size={12}>
        <TextInput source="observacao" parse={vazioParaNulo} multiline fullWidth />
      </Grid>
      {!rapido && (
        <Grid size={12}>
          <BooleanInput source="ativo" defaultValue={true} helperText="Inativo deixa de aparecer nas listas de escolha." />
        </Grid>
      )}
    </Grid>
  );
}

export function FornecedorCriar() {
  return (
    <Create title="Novo fornecedor" redirect="edit">
      <SimpleForm>
        <CamposFornecedor />
      </SimpleForm>
    </Create>
  );
}

/** Últimos preços cotados com este fornecedor. */
function CotacoesDoFornecedor() {
  const registro = useRecordContext();
  const celular = useCelular();
  if (!registro) return null;
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
          Cotações deste fornecedor
        </Typography>
        <ReferenceManyField
          reference="v_cotacao_preco"
          target="fornecedor_id"
          sort={{ field: "data_cotacao", order: "DESC" }}
          perPage={50}
          empty={<Typography variant="body2" color="text.secondary">Nenhuma cotação registrada.</Typography>}
        >
          {celular ? (
            <SimpleList
              primaryText={(r) => (
                <ReferenceField record={r} source="produto_id" reference="produto" link={false}>
                  <TextField source="descricao" />
                </ReferenceField>
              )}
              secondaryText={(r) => `${r.unidade}: ${formatarMoeda(r.custo_avista)} · ${formatarDataHora(r.data_cotacao)}`}
              tertiaryText={() => <ChipSituacaoCotacao />}
              linkType={false}
            />
          ) : (
            <Datagrid bulkActionButtons={false} rowClick={false}>
              <ReferenceField source="produto_id" reference="produto" label="Produto">
                <TextField source="descricao" />
              </ReferenceField>
              <FunctionField label="Embalagem" render={(r) => `${r.unidade} (${formatarNumero(r.fator_base)})`} />
              <FunctionField label="Custo à vista" render={(r) => formatarMoeda(r.custo_avista)} />
              <FunctionField label="Equivalente por unidade-base" render={(r) => formatarMoeda(r.custo_unit_base)} />
              <FunctionField label="Data" render={(r) => formatarDataHora(r.data_cotacao)} />
              <FunctionField label="Situação" render={() => <ChipSituacaoCotacao />} />
            </Datagrid>
          )}
        </ReferenceManyField>
      </CardContent>
    </Card>
  );
}

export function FornecedorEditar() {
  return (
    <Edit title="Editar fornecedor" mutationMode="pessimistic" redirect={false}>
      <SimpleForm toolbar={<BarraFormulario />}>
        <CamposFornecedor />
      </SimpleForm>
      <CotacoesDoFornecedor />
    </Edit>
  );
}

/* ------------------------- Grupos de produto e setores -------------------------
 * Ficam na tela "Parâmetros do sistema" (não têm item próprio no menu). Depois de salvar, volta para lá.
 */

type RecursoLista = "grupo_produto" | "setor_fornecedor" | "fabricante" | "unidade_medida";

function ListaSimples({ recurso, titulo }: { recurso: RecursoLista; titulo: string }) {
  const unidade = recurso === "unidade_medida";
  return (
    <List
      resource={recurso}
      title={false}
      sort={{ field: unidade ? "sigla" : "nome", order: "ASC" }}
      actions={false}
      exporter={false}
      perPage={100}
      pagination={false}
      disableSyncWithLocation
      storeKey={`parametros.${recurso}`}
      empty={false}
      component="div"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 2 }}>
        <Typography variant="h6" sx={{ fontSize: "1rem" }}>
          {titulo}
        </Typography>
        <Button component={Link} to={`/${recurso}/create`} size="small" startIcon={<AddIcon />}>
          Adicionar
        </Button>
      </Stack>
      <Datagrid bulkActionButtons={false} rowClick="edit">
        {unidade && <TextField source="sigla" />}
        <TextField source={unidade ? "descricao" : "nome"} />
        <BooleanField source="ativo" />
      </Datagrid>
    </List>
  );
}

export function GruposESetores() {
  const listas: [RecursoLista, string][] = [
    ["grupo_produto", "Grupos de produto"],
    ["setor_fornecedor", "Setores de fornecedor"],
    ["fabricante", "Fabricantes"],
    ["unidade_medida", "Unidades de medida"],
  ];
  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {listas.map(([recurso, titulo]) => (
        <Grid size={{ xs: 12, md: 6 }} key={recurso}>
          <Card sx={{ maxHeight: 520, overflow: "auto" }}>
            <ListaSimples recurso={recurso} titulo={titulo} />
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function FormularioNomeAtivo() {
  return (
    <SimpleForm toolbar={<BarraFormulario />}>
      <TextInput source="nome" validate={required()} fullWidth />
      <BooleanInput source="ativo" defaultValue={true} helperText="Inativo deixa de aparecer nas listas de escolha." />
    </SimpleForm>
  );
}

const voltarParametros = () => "parametro_sistema";

export function GrupoCriar() {
  return (
    <Create title="Novo grupo de produto" redirect={voltarParametros}>
      <FormularioNomeAtivo />
    </Create>
  );
}

export function GrupoEditar() {
  return (
    <Edit title="Editar grupo de produto" mutationMode="pessimistic" redirect={voltarParametros}>
      <FormularioNomeAtivo />
    </Edit>
  );
}

export function SetorCriar() {
  return (
    <Create title="Novo setor de fornecedor" redirect={voltarParametros}>
      <FormularioNomeAtivo />
    </Create>
  );
}

export function SetorEditar() {
  return (
    <Edit title="Editar setor de fornecedor" mutationMode="pessimistic" redirect={voltarParametros}>
      <FormularioNomeAtivo />
    </Edit>
  );
}

export function FabricanteCriar() {
  return (
    <Create title="Novo fabricante" redirect={voltarParametros}>
      <FormularioNomeAtivo />
    </Create>
  );
}

export function FabricanteEditar() {
  return (
    <Edit title="Editar fabricante" mutationMode="pessimistic" redirect={voltarParametros}>
      <FormularioNomeAtivo />
    </Edit>
  );
}

const maiusculaSigla = (v: unknown) => String(v ?? "").toUpperCase().replace(/\s/g, "");
const validarSigla = regex(/^[A-Z0-9ÇÃÕÁÉÍÓÚÂÊÔ]{1,12}$/, "Até 12 letras ou números, sem espaços.");

function FormularioUnidade() {
  return (
    <SimpleForm toolbar={<BarraFormulario />}>
      <TextInput source="sigla" parse={maiusculaSigla} validate={[required(), validarSigla]} helperText="Ex.: CX, PCT, FD." />
      <TextInput source="descricao" validate={required()} fullWidth />
      <BooleanInput source="ativo" defaultValue={true} helperText="Inativa deixa de aparecer nas listas de escolha." />
    </SimpleForm>
  );
}

export function UnidadeCriar() {
  return (
    <Create title="Nova unidade de medida" redirect={voltarParametros}>
      <FormularioUnidade />
    </Create>
  );
}

export function UnidadeEditar() {
  return (
    <Edit title="Editar unidade de medida" mutationMode="pessimistic" redirect={voltarParametros}>
      <FormularioUnidade />
    </Edit>
  );
}
